import type { ProblemTemplate } from '../../types.ts';
import type { Rng } from '../../math/rng.ts';
import {
  binomial,
  choose,
  exactDiv,
  integerProblem,
  ipow,
  leadTerm,
  letter,
  linear,
  monic,
  nextTerm,
  scaled,
  sharingFactorWith,
  signedBlank,
} from './helpers-g09-10.ts';

/**
 * Grade 10 templates (CAPS FET, first year): factorising trinomials and the
 * difference of two squares, quadratic equations by factorisation,
 * simultaneous linear equations, simple exponential equations and linear
 * inequalities.
 *
 * Ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE.
 * Every answer is a whole number. Every template sets `allowNegative`: even
 * where the answer is always positive, a negative choice is a genuine
 * sign-slip misconception at this level.
 */

/** `x² + bx + c = (x + p)(x + □)`, p, q > 0. */
const factorTrinomial: ProblemTemplate = {
  id: 'g10.factor-trinomial',
  grade: 10,
  title: 'Factorise x² + bx + c',
  parTimeMs: 16000,
  allowNegative: true,
  generate(rng, d) {
    const hi = scaled(d, 6, 10);
    const p = rng.int(2, hi); // p = 1 would make the answer a copy of the constant term
    const q = rng.intExcept(1, hi, [p]);
    const v = letter(rng, d);
    return integerProblem(`${monic(v, p + q, p * q)} = ${binomial(1, v, p)}(${v} + \\box)`, q, [
      p * q - p, // subtracted the known number from the constant instead of dividing
      p + q, // copied the middle coefficient
      -q, // sign slip
      p, // repeated the given factor
    ]);
  },
};

/**
 * `x² + bx + c = (x + p)(x ± □)` with at least one negative number. A negative
 * second factor is shown as `(x - □)`, so the box always holds its size.
 */
const factorTrinomialNeg: ProblemTemplate = {
  id: 'g10.factor-trinomial-neg',
  grade: 10,
  title: 'Factorise x² + bx + c with negatives',
  parTimeMs: 18000,
  allowNegative: true,
  generate(rng, d) {
    const hi = scaled(d, 6, 10);
    const pm = rng.int(2, hi); // |p| = 1 would make the answer a copy of the constant term
    const qm = rng.intExcept(1, hi, [pm]); // distinct sizes: the middle term never vanishes
    let p = pm;
    let q = qm;
    if (d >= 0.5 && rng.chance(0.35)) {
      p = -pm;
      q = -qm;
    } else if (rng.chance(0.5)) p = -pm;
    else q = -qm;
    const v = letter(rng, d);
    // The unknown factor is written the conventional way: (x - 4), never (x + □) with □ = −4.
    const blank = signedBlank(q);
    return integerProblem(`${monic(v, p + q, p * q)} = ${binomial(1, v, p)}(${v}${blank.sign}\\box)`, blank.answer, [
      blank.fill(-q), // sign slip — the classic: the factor's sign flipped
      blank.fill(p + q + p), // took the known number away from b with the wrong sign
      blank.fill(-p), // used the opposite of the given number
      blank.fill(p * q - p), // subtracted instead of dividing the constant
    ]);
  },
};

/**
 * Difference of two squares: fill the number (or, higher up, the square or the
 * coefficient) in `x² − a² = (x − a)(x + a)`.
 */
const diffSquares: ProblemTemplate = {
  id: 'g10.diff-squares',
  grade: 10,
  title: 'Difference of two squares',
  parTimeMs: 10000,
  allowNegative: true,
  generate(rng, d) {
    const v = letter(rng, d);
    const form = choose(rng, [
      ['second', 3],
      ['first', 2],
      ['square', d >= 0.3 ? 2 : 0],
      ['coefficient', d >= 0.5 ? 3 : 0],
      ['fill-coefficient', d >= 0.7 ? 2 : 0],
    ] as const);

    if (form === 'second' || form === 'first') {
      const a = rng.int(2, scaled(d, 9, 12));
      const lhs = `${v}^2 - ${a * a}`;
      const prompt =
        form === 'second' ? `${lhs} = (${v} - ${a})(${v} + \\box)` : `${lhs} = (${v} - \\box)(${v} + ${a})`;
      return integerProblem(prompt, a, [
        a * a, // did not take the square root
        -a, // sign slip (that would give a perfect square)
        exactDiv(a * a, 2), // halved instead of taking the square root
      ]);
    }

    if (form === 'square') {
      // x² − □ = (x − 7)(x + 7) → 49: the factors are given, the square is not.
      const a = rng.int(2, scaled(d, 9, 12));
      return integerProblem(`${v}^2 - \\box = (${v} - ${a})(${v} + ${a})`, a * a, [
        2 * a, // doubled instead of squaring
        a, // did not square at all
        -a * a, // thought (x − a)(x + a) gives x² + a², so the blank must be negative
      ]);
    }

    const b = rng.int(2, scaled(d, 3, 5));
    const aHi = scaled(d, 7, 9);
    const a = rng.intExcept(1, aHi, sharingFactorWith(b, 1, aHi)); // coprime: no common factor to take out first
    const lhs = `${b * b}${v}^2 - ${a * a}`;
    if (form === 'coefficient') {
      return integerProblem(`${lhs} = (${b}${v} - ${a})(${b}${v} + \\box)`, a, [
        a * a, // did not take the square root
        -a, // sign slip
        a * b, // multiplied by the coefficient
      ]);
    }
    return integerProblem(`${lhs} = (${b}${v} - ${a})(\\box ${v} + ${a})`, b, [
      b * b, // did not take the square root of the coefficient
      -b, // sign slip
      a, // mixed up the coefficient and the constant
    ]);
  },
};

/** Monic quadratic with two distinct integer roots; give the larger or the smaller one. */
const quadraticRoot: ProblemTemplate = {
  id: 'g10.quadratic-root',
  grade: 10,
  title: 'Solve a quadratic by factorising',
  parTimeMs: 20000,
  allowNegative: true,
  generate(rng, d) {
    const hi = scaled(d, 6, 12);
    const lo = d < 0.35 ? 1 : -hi; // positive roots in the gentle band
    const r1 = rng.int(lo, hi);
    // A double root would make "larger" meaningless; x² − 1 = 0 is left out because ±1 and
    // 1² collapse every misconception onto the answer or the other root.
    const r2 = rng.intExcept(lo, hi, Math.abs(r1) === 1 ? [r1, -r1] : [r1]);
    const b = -(r1 + r2);
    const c = r1 * r2;
    const v = letter(rng, d);
    const moved = d >= 0.7 && b !== 0 && c !== 0 && rng.chance(0.35);
    const prompt = moved ? `${v}^2${nextTerm(b, v)} = ${-c}` : `${monic(v, b, c)} = 0`;
    const larger = rng.chance(0.5);
    const answer = larger ? Math.max(r1, r2) : Math.min(r1, r2);
    const other = larger ? Math.min(r1, r2) : Math.max(r1, r2);
    return integerProblem(
      prompt,
      answer,
      [
        other, // gave the other root
        -answer, // sign slip: (x - 3) = 0 read as x = -3
        -other, // both slips
        b === 0 ? Math.sign(answer) * answer * answer : null, // x² = a² with no x-term: forgot the square root
      ],
      { key: larger ? 'largerRoot' : 'smallerRoot' },
    );
  },
};

/** One linear equation `a·x + b·y = c` as markup. */
function equation(a: number, b: number, c: number): string {
  return `${leadTerm(a, 'x')}${nextTerm(b, 'y')} = ${c}`;
}

/** Coefficients and solution of a 2×2 system with a non-zero determinant. */
interface System {
  a1: number;
  b1: number;
  a2: number;
  b2: number;
  x: number;
  y: number;
}

/** Tiered systems: add/subtract to eliminate → one coefficient → general coefficients and negative solutions. */
function makeSystem(rng: Rng, d: number): System {
  if (d < 0.35) {
    // x + y = s; x - y = t
    const x = rng.int(2, scaled(d, 9, 12));
    return { a1: 1, b1: 1, a2: 1, b2: -1, x, y: rng.int(1, x - 1) };
  }
  if (d < 0.7) {
    // 2x + y = 11; x - y = 1  (a1·b2 − 1 ≠ 0 for every pick)
    const x = rng.int(1, 9);
    return { a1: rng.int(2, 3), b1: 1, a2: 1, b2: rng.pick([-1, 1, -2]), x, y: rng.intExcept(1, 9, [x]) };
  }
  const coef = () => rng.intExcept(-4, 5, [0]);
  const a1 = coef();
  const b1 = coef();
  const a2 = coef();
  const singular = exactDiv(b1 * a2, a1); // the b2 that would make a1·b2 = b1·a2
  const b2 = rng.intExcept(-4, 5, singular === null ? [0] : [0, singular]);
  const x = rng.intExcept(-9, 9, [0]);
  // y ≠ ±x keeps "found the other unknown" and "sign slip" as two different wrong answers.
  return { a1, b1, a2, b2, x, y: rng.intExcept(-9, 9, [0, x, -x]) };
}

/** Two linear equations in x and y; find the one asked for. */
const simultaneous: ProblemTemplate = {
  id: 'g10.simultaneous',
  grade: 10,
  title: 'Simultaneous linear equations',
  parTimeMs: 20000,
  allowNegative: true,
  generate(rng, d) {
    const s = makeSystem(rng, d);
    const c1 = s.a1 * s.x + s.b1 * s.y;
    const c2 = s.a2 * s.x + s.b2 * s.y;
    const eq1 = equation(s.a1, s.b1, c1);
    const eq2 = equation(s.a2, s.b2, c2);
    const [first, second] = d >= 0.35 && rng.chance(0.5) ? [eq2, eq1] : [eq1, eq2];
    const askX = rng.chance(0.5);
    const answer = askX ? s.x : s.y;
    const det = s.a1 * s.b2 - s.b1 * s.a2;
    // The result of the classic slip when subtracting the equations: the second constant's sign flips.
    const slip = askX ? exactDiv(c1 * s.b2 + s.b1 * c2, det) : exactDiv(-s.a1 * c2 - c1 * s.a2, det);
    const believable = (v: number | null) => (v !== null && Math.abs(v) <= 40 ? v : null);
    return integerProblem(
      `${first} \\sep ${second}`,
      answer,
      [
        askX ? s.y : s.x, // found the other unknown
        -answer, // sign slip
        Math.abs(det) > 1 ? believable(answer * Math.abs(det)) : null, // forgot to divide after eliminating
        believable(slip), // subtracted the constants with the wrong sign
      ],
      { key: 'simultaneous', params: { v: askX ? 'x' : 'y' } },
    );
  },
};

/** `b^{mx + k} = c` with a whole-number solution (c may be 1 or 1/bⁿ higher up). */
const exponentialEq: ProblemTemplate = {
  id: 'g10.exponential-eq',
  grade: 10,
  title: 'Exponential equation',
  parTimeMs: 12000,
  allowNegative: true,
  generate(rng, d) {
    const b = rng.pick(d < 0.5 ? [2, 3, 5] : [2, 3, 4, 5, 6, 7, 10]);
    const cap = scaled(d, 32, 1000);
    let eMax = 0;
    while (ipow(b, eMax + 1) <= cap) eMax++;
    const eMin = d >= 0.6 ? -3 : 1; // right side 1 or a unit fraction only at the top of the range
    const xMin = d >= 0.6 ? -4 : 1;
    const m = d >= 0.6 && rng.chance(0.4) ? rng.int(2, 3) : 1;
    const ks = d < 0.25 ? [0] : [-3, -2, -1, 0, 1, 2, 3];
    const options: { x: number; k: number }[] = [];
    for (const k of ks) {
      for (let x = xMin; x <= 8; x++) {
        const e = m * x + k;
        const trivial = m === 1 && k === 0 && x === 1; // b^x = b
        const idleCoefficient = m > 1 && x === 0; // 4^{3x + 2} = 16: the 3 plays no part
        if (e >= eMin && e <= eMax && !trivial && !idleCoefficient) options.push({ x, k });
      }
    }
    // Fallback keeps e = 1, which is always in range (b ≤ cap).
    const { x, k } = options.length ? rng.pick(options) : { x: 1, k: 1 - m };
    const e = m * x + k;
    const v = letter(rng, d);
    const rhs = e > 0 ? `${ipow(b, e)}` : e === 0 ? '1' : `\\frac{1}{${ipow(b, -e)}}`;
    return integerProblem(
      `${b}^{${linear(m, v, k)}} = ${rhs}`,
      x,
      [
        e > 0 ? exactDiv(ipow(b, e), b) : null, // divided by the base instead of finding the power
        e, // stopped at the exponent (did not undo + k or the coefficient)
        exactDiv(e + k, m), // moved k across with the wrong sign
        m > 1 ? e - k : null, // forgot to divide by the coefficient of x
        -x, // sign slip
        x - 1, // miscounted the repeated multiplication (one short)…
        x + 1, // …or one over
      ],
      { key: 'solve', params: { v } },
    );
  },
};

const FLIP = { '>': '<', '<': '>', '\\ge': '\\le', '\\le': '\\ge' } as const;
type Relation = keyof typeof FLIP;

/** Linear inequality: give the boundary of the solution set (the solved form, flipped sign included, is shown). */
const inequality: ProblemTemplate = {
  id: 'g10.inequality',
  grade: 10,
  title: 'Linear inequality',
  parTimeMs: 14000,
  allowNegative: true,
  generate(rng, d) {
    const rel: Relation = rng.pick(d < 0.3 ? (['>', '<'] as const) : (['>', '<', '\\ge', '\\le'] as const));
    const x = d < 0.35 ? rng.int(1, 9) : rng.intExcept(-9, 12, [0]);
    const v = letter(rng, d);
    const form = choose(rng, [
      ['positive', 3],
      ['negative', d >= 0.6 ? 3 : 0],
      ['both-sides', d >= 0.75 ? 2 : 0],
    ] as const);

    let lhs: string;
    let rhs: string;
    let coef: number; // coefficient of x once everything is collected on the left
    let distractors: (number | null)[];
    if (form === 'both-sides') {
      // a1·x + b1 REL a2·x + b2
      const a1 = rng.int(2, 7);
      const a2 = rng.intExcept(1, 7, [a1 - 1, a1, a1 + 1]); // |a1 − a2| ≥ 2 keeps a real division step
      const b1 = rng.intExcept(-9, 9, [0]);
      coef = a1 - a2;
      const b2 = b1 + coef * x;
      lhs = linear(a1, v, b1);
      rhs = linear(a2, v, b2);
      distractors = [
        exactDiv(b2 + b1, coef), // moved the constant with the wrong sign
        b2 - b1, // forgot to divide by the coefficient
        exactDiv(b2 - b1, a1 + a2), // added the x-coefficients instead of subtracting
      ];
    } else {
      // a·x + b REL c
      coef = form === 'negative' ? -rng.int(2, 6) : rng.int(2, scaled(d, 5, 9));
      const bHi = scaled(d, 9, 15);
      // Gentle band: the right-hand side stays positive (b ≥ 1 − a·x).
      const b = rng.intExcept(d < 0.35 ? Math.max(-bHi, 1 - coef * x) : -bHi, bHi, [0]);
      const c = coef * x + b;
      lhs = linear(coef, v, b);
      rhs = `${c}`;
      distractors = [
        exactDiv(c + b, coef), // moved the constant with the wrong sign
        c - b, // forgot to divide by the coefficient
        exactDiv(c, coef) === null ? null : c / coef - b, // divided before subtracting
      ];
    }
    const solvedRel = coef < 0 ? FLIP[rel] : rel; // dividing by a negative flips the sign
    return integerProblem(
      `${lhs} ${rel} ${rhs} \\Rightarrow ${v} ${solvedRel} \\box`,
      x,
      [...distractors, -x /* sign slip when dividing */],
      { key: 'boundary' },
    );
  },
};

/** Grade 10 templates — ids and specs: docs/CONTENT_CATALOGUE.md and levels/levels.ts TEMPLATE_CATALOGUE. */
export const GRADE_10: ProblemTemplate[] = [
  factorTrinomial,
  factorTrinomialNeg,
  diffSquares,
  quadraticRoot,
  simultaneous,
  exponentialEq,
  inequality,
];
